import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { WidgetDataResult, WidgetChartConfig } from '../types';
import { formatNumber, escapeHtml } from './echartsUtils';
import { ReactComponent as TrendUpIcon } from '../assets/arrowUp.svg';
import { ReactComponent as TrendDownIcon } from '../assets/arrowDown.svg';
import styles from './ChartMetric.module.scss';

interface ChartMetricProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
}

function formatMainValue(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Detect the first numeric column from data */
function findNumericCol(data: WidgetDataResult, exclude?: string): string | undefined {
  return data.columns.find((c) => {
    if (c === exclude) return false;
    const v = data.rows[0]?.[c];
    return typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(Number(v)));
  });
}

/** Detect the date/time column from data */
function findDateCol(data: WidgetDataResult, excludeCols: string[]): string | undefined {
  return data.columns.find((c) => {
    if (excludeCols.includes(c)) return false;
    const v = data.rows[0]?.[c];
    return typeof v === 'string' && /^\d{4}-\d{2}/.test(v);
  });
}

export const ChartMetric: React.FC<ChartMetricProps> = ({ data, config }) => {
  const metricCfg = config.metricConfig ?? {};
  const mapping = config.columnMapping ?? {};
  const trendRange = metricCfg.trendRange ?? 7;
  const trendLabel = metricCfg.trendLabel ?? `last ${trendRange} days`;
  const sparklineColor = metricCfg.sparklineColor ?? '#3B83F6';

  const { mainValue, trendPercent, sparklineValues, sparklineDates, isTimeSeries } = useMemo(() => {
    if (data.rows.length === 0) {
      return { mainValue: null, trendPercent: null, sparklineValues: [], sparklineDates: [], isTimeSeries: false };
    }

    const valueCol = mapping.mainValue ?? findNumericCol(data) ?? data.columns[0];
    const trendCol = mapping.trendPercent ?? null;
    const dateCol = mapping.xAxis ?? findDateCol(data, [valueCol]);

    // Single-row mode
    if (data.rows.length === 1) {
      const row = data.rows[0];
      const val = Number(row[valueCol]) || 0;
      const trend = trendCol != null ? Number(row[trendCol]) : null;
      return { mainValue: val, trendPercent: trend, sparklineValues: [], sparklineDates: [], isTimeSeries: false };
    }

    // Time-series mode
    const values = data.rows.map((r) => Number(r[valueCol]) || 0);
    const dates = dateCol
      ? data.rows.map((r) => String(r[dateCol] ?? ''))
      : data.rows.map((_, i) => String(i));

    const mode = metricCfg.mainValueMode ?? 'sum';
    let main: number;
    if (mode === 'sum') main = values.reduce((a, b) => a + b, 0);
    else if (mode === 'avg') main = values.reduce((a, b) => a + b, 0) / values.length;
    else main = values[values.length - 1]; // 'last'

    // Trend: pre-computed column OR auto-computed (last vs first)
    let trend: number | null = null;
    if (trendCol != null) {
      trend = Number(data.rows[data.rows.length - 1][trendCol]);
    } else if (values.length >= 2) {
      const first = values[0];
      const last = values[values.length - 1];
      if (first !== 0) trend = ((last - first) / Math.abs(first)) * 100;
    }

    return { mainValue: main, trendPercent: trend, sparklineValues: values, sparklineDates: dates, isTimeSeries: true };
  }, [data, mapping, metricCfg]);

  const showSparkline = isTimeSeries && metricCfg.showSparkline !== false && sparklineValues.length > 0;

  const sparklineOption = useMemo(() => ({
    animation: false,
    tooltip: {
      trigger: 'axis' as const,
      appendToBody: true,
      axisPointer: { type: 'line' as const },
      formatter: (params: { name: string; value: number }[]) => {
        const { name, value } = params[0];
        return `${escapeHtml(name)}: <b>${formatNumber(value)}</b>`;
      },
    },
    grid: { left: 0, right: 0, top: 4, bottom: 4 },
    xAxis: { type: 'category' as const, show: false, data: sparklineDates, boundaryGap: false },
    yAxis: { type: 'value' as const, show: false },
    series: [
      {
        type: 'line' as const,
        data: sparklineValues,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        lineStyle: { color: sparklineColor, width: 2 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: `${sparklineColor}44` },
              { offset: 1, color: `${sparklineColor}00` },
            ],
          },
        },
      },
    ],
  }), [sparklineDates, sparklineValues, sparklineColor]);

  if (mainValue === null) return null;

  const trendUp = trendPercent !== null && trendPercent >= 0;
  const hasTrend = trendPercent !== null;
  const trendClass = trendUp ? styles.trendUp : styles.trendDown;

  const formattedValue = `${metricCfg.valuePrefix ?? ''}${formatMainValue(mainValue)}${metricCfg.valueSuffix ?? ''}`;

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div className={styles.valueSection}>
          <div className={styles.mainValue} aria-label={formattedValue}>
            {formattedValue}
          </div>
        </div>
        {showSparkline && (
          <div className={styles.sparkline} aria-hidden="true">
            <ReactECharts
              option={sparklineOption}
              style={{ width: '100%', height: '100%' }}
              opts={{ renderer: 'svg' }}
            />
          </div>
        )}
      </div>
      {hasTrend && (
        <div className={`${styles.trend} ${trendClass}`} aria-label={`Trend: ${trendUp ? 'up' : 'down'} ${Math.abs(trendPercent!).toFixed(1)}%, ${trendLabel}`}>
          <span className={styles.trendArrow} aria-hidden="true">
            {trendUp ? (
                <TrendUpIcon />
            ) : (
                <TrendDownIcon />
            )}
          </span>
          <span className={styles.trendPercent}>
            {Math.abs(trendPercent!).toFixed(1)}%
          </span>
          <span className={styles.trendRangeLabel}>{trendLabel}</span>
        </div>
      )}
    </div>
  );
};
